import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, Trash2, Send, Loader2, Megaphone } from "lucide-react";
import { LOGO_URL, BRAND } from "../data/menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API, withCredentials: true });

const dateFmt = (iso) => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return "";
  }
};

const LoginView = ({ onLoggedIn }) => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    try {
      await api.post("/admin/login", { password });
      toast.success("Signed in");
      onLoggedIn();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Sign-in failed";
      toast.error(typeof msg === "string" ? msg : "Sign-in failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-5">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        noValidate
        className="w-full max-w-md border border-white/10 rounded-lg p-8 md:p-10 bg-[#141414]"
      >
        <div className="flex flex-col items-center gap-4 mb-8">
          <div className="rounded-md bg-black p-3">
            <img src={LOGO_URL} alt={BRAND} className="h-10 w-auto object-contain" />
          </div>
          <h1 className="font-display uppercase text-3xl md:text-4xl leading-none tracking-tight">
            Owner Sign-In
          </h1>
          <p className="text-sm text-[#a8a8a8] text-center">
            Post announcements your customers will see on the home page.
          </p>
        </div>

        <label htmlFor="admin-password" className="block font-body uppercase tracking-widest text-xs text-[#a8a8a8] mb-2">
          Password
        </label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#a8a8a8]" />
          <input
            id="admin-password"
            data-testid="admin-password-input"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-[#0a0a0a] border border-white/10 rounded-md pl-10 pr-4 py-3.5 font-body text-white outline-none transition-colors focus:border-[#ffb703] focus:ring-2 focus:ring-[#ffb703]/30"
            placeholder="••••••••"
          />
        </div>

        <button
          type="submit"
          data-testid="admin-login-submit"
          disabled={loading || !password}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#e63946] px-6 py-3.5 font-accent uppercase tracking-wide text-white text-base transition-all duration-300 hover:bg-[#ffb703] hover:text-black focus:outline-none focus:ring-2 focus:ring-[#ffb703] disabled:opacity-60 disabled:hover:bg-[#e63946] disabled:hover:text-white"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
          {loading ? "Signing in…" : "Sign In"}
        </button>

        <a href="/" className="mt-6 block text-center text-xs uppercase tracking-widest text-[#a8a8a8] hover:text-white">
          ← Back to site
        </a>
      </form>
    </div>
  );
};

const DashboardView = ({ onLogout }) => {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [items, setItems] = useState([]);
  const [posting, setPosting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/messages");
      setItems(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Couldn't load announcements — check your connection.");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const post = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) {
      toast.error("Add a title and message body.");
      return;
    }
    setPosting(true);
    try {
      await api.post("/admin/messages", { title, body });
      setTitle("");
      setBody("");
      toast.success("Announcement posted");
      load();
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        onLogout();
      } else {
        toast.error("Could not post. Please try again.");
      }
    } finally {
      setPosting(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    try {
      await api.delete(`/admin/messages/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      if (err?.response?.status === 401) {
        onLogout();
      } else {
        toast.error("Could not delete");
      }
    }
  };

  const doLogout = async () => {
    try {
      await api.post("/admin/logout");
    } catch {
      /* ignore */
    }
    onLogout();
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <header className="border-b border-white/10 sticky top-0 bg-black/70 backdrop-blur-xl z-40">
        <div className="mx-auto max-w-[1200px] px-5 md:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="rounded-md bg-black p-1.5">
              <img src={LOGO_URL} alt={BRAND} className="h-9 w-auto object-contain" />
            </div>
            <span className="font-display uppercase tracking-tight text-lg leading-none">
              Owner Console
            </span>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              className="hidden sm:inline text-xs uppercase tracking-widest text-[#a8a8a8] hover:text-white"
            >
              View Site
            </a>
            <button
              onClick={doLogout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs uppercase tracking-widest hover:bg-white/10"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] px-5 md:px-8 py-10 md:py-14 grid grid-cols-1 lg:grid-cols-5 gap-8">
        <section className="lg:col-span-2">
          <div className="flex items-center gap-3 mb-6">
            <Megaphone className="h-6 w-6 text-[#ffb703]" />
            <h2 className="font-display uppercase text-2xl md:text-3xl leading-none tracking-tight">
              Post an announcement
            </h2>
          </div>
          <form onSubmit={post} data-testid="admin-post-form" noValidate className="space-y-4">
            <div>
              <label htmlFor="msg-title" className="block font-body uppercase tracking-widest text-xs text-[#a8a8a8] mb-2">
                Title <span className="text-[#e63946]">*</span>
              </label>
              <input
                id="msg-title"
                data-testid="admin-msg-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Closed this weekend"
                className="w-full bg-[#141414] border border-white/10 rounded-md px-4 py-3 outline-none focus:border-[#ffb703] focus:ring-2 focus:ring-[#ffb703]/30"
              />
            </div>
            <div>
              <label htmlFor="msg-body" className="block font-body uppercase tracking-widest text-xs text-[#a8a8a8] mb-2">
                Message <span className="text-[#e63946]">*</span>
              </label>
              <textarea
                id="msg-body"
                data-testid="admin-msg-body"
                rows={5}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Share news, hours, specials or a note for your customers…"
                className="w-full bg-[#141414] border border-white/10 rounded-md px-4 py-3 outline-none focus:border-[#ffb703] focus:ring-2 focus:ring-[#ffb703]/30 resize-none"
              />
            </div>
            <button
              type="submit"
              data-testid="admin-post-submit"
              disabled={posting}
              className="inline-flex items-center gap-2 rounded-full bg-[#e63946] px-6 py-3 font-accent uppercase tracking-wide text-white transition-all duration-300 hover:bg-[#ffb703] hover:text-black focus:outline-none focus:ring-2 focus:ring-[#ffb703] disabled:opacity-60"
            >
              {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {posting ? "Posting…" : "Publish"}
            </button>
          </form>
        </section>

        <section className="lg:col-span-3">
          <h2 className="font-display uppercase text-2xl md:text-3xl leading-none tracking-tight mb-6">
            Your announcements ({items.length})
          </h2>
          {items.length === 0 ? (
            <p className="text-[#a8a8a8] text-sm">
              No announcements yet. Publish your first one to show it on the home page.
            </p>
          ) : (
            <ul className="space-y-3">
              {items.map((m) => (
                <li
                  key={m.id}
                  data-testid={`admin-message-${m.id}`}
                  className="border border-white/10 rounded-md p-5 bg-[#141414] flex items-start justify-between gap-4"
                >
                  <div className="min-w-0">
                    <div className="flex items-baseline gap-3 mb-1">
                      <h3 className="font-display uppercase text-lg leading-none tracking-tight">{m.title}</h3>
                      <span className="text-[10px] uppercase tracking-widest text-[#a8a8a8]">
                        {dateFmt(m.created_at)}
                      </span>
                    </div>
                    <p className="text-sm text-[#d1d1d1] whitespace-pre-line break-words">{m.body}</p>
                  </div>
                  <button
                    onClick={() => remove(m.id)}
                    data-testid={`admin-delete-${m.id}`}
                    className="shrink-0 inline-flex items-center gap-1 text-xs uppercase tracking-widest text-[#ffb703] hover:text-[#e63946]"
                  >
                    <Trash2 className="h-4 w-4" /> Delete
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
};

export default function AdminPage() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);

  const check = () =>
    api
      .get("/admin/me")
      .then(() => setAuthed(true))
      .catch(() => setAuthed(false))
      .finally(() => setChecking(false));

  useEffect(() => {
    check();
  }, []);

  const logout = () => setAuthed(false);

  if (checking) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#ffb703]" />
      </div>
    );
  }

  return authed ? (
    <DashboardView onLogout={logout} />
  ) : (
    <LoginView onLoggedIn={() => setAuthed(true)} />
  );
}
